import { useState } from "react";
import { Globe, Plus, Sparkles, X } from "lucide-react";
import { Button } from "@/components/form/Button";
import { cn } from "@/utils/helpers";
import { MEDIA_TYPE_LABEL, displayAspectRatio, type CalendarPlanSlot } from "@/types/contentCalendar.type";
import { KitIconButton, KitInput, KitTextAction, KitTextarea } from "../../components/kit";
import { shortDate } from "../postState";
import IdeaMediaDialog, { type MediaChoice } from "./IdeaMediaDialog";
import "../components/postPage.css";

export type WriteOwnDraft = {
  title: string;
  /** One to five, as the idea stores them; the first is the one the client sees. */
  hooks: string[];
  description: string;
  /** What the post is for: the Objective card on the slide. Optional. */
  objective: string;
  /** Picked from References or saved to them in the dialog; the first is the idea's reference. */
  references: MediaChoice[];
};

export const EMPTY_WRITE_OWN: WriteOwnDraft = { title: "", hooks: [""], description: "", objective: "", references: [] };

/** The backend's caps on a hand-written idea (v_CreateManualIdea). */
const TITLE_MAX = 160;
const HOOK_MAX = 300;
const HOOKS_MAX = 5;
const DESCRIPTION_MAX = 600;
const OBJECTIVE_MAX = 600;
const REFERENCES_MAX = 4;

/** A picked reference as the composer's link card (Chat / Link card · In the composer). */
function ReferenceChip({ choice, onRemove }: { choice: MediaChoice; onRemove: () => void }) {
  const still = choice.imageUrl ?? choice.thumbnailUrl;
  return (
    <div className="flex h-[52px] w-full max-w-[340px] items-center gap-2.5 rounded-card bg-co-chrome-solid p-2 shadow-[inset_0_0_0_1px_var(--co-line)]">
      <span className="grid size-9 shrink-0 place-items-center overflow-hidden rounded-[8px] bg-co-recess text-co-muted shadow-[inset_0_0_0_1px_var(--co-line)]">
        {still ? (
          <img src={still} alt="" className="size-full object-cover" />
        ) : choice.videoUrl ? (
          <video src={`${choice.videoUrl}#t=0.1`} muted playsInline preload="metadata" className="size-full object-cover" />
        ) : (
          <Globe className="size-4" strokeWidth={1.7} aria-hidden="true" />
        )}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-[12.5px] font-medium leading-[17px] text-co-text">{choice.label}</span>
        <span className="truncate text-[11.5px] leading-4 text-co-muted">{choice.kindLine}</span>
      </span>
      <button
        type="button"
        aria-label={`Remove ${choice.label}`}
        onClick={onRemove}
        className="grid size-5 shrink-0 place-items-center text-co-muted outline-none hover:text-co-text focus-visible:outline-2 focus-visible:outline-co-accent"
      >
        <X className="size-[13px]" strokeWidth={1.8} aria-hidden="true" />
      </button>
    </div>
  );
}

/**
 * Write your own idea (Figma 08, 3 Ideas; CEO note 2: "Manual input, Creo
 * doesn't expand"). A plain form in the slide's place: Title, Hook,
 * Description, and optional references. Write brief makes it the pick and
 * Creo writes the brief from it, as for a picked idea; the words stay as
 * written. Asking Creo to write it is the side chat's job.
 */
export default function WriteOwnIdeaPanel({
  slot,
  brandId,
  phone,
  initial,
  backLabel = "Back to the three ideas",
  busy = false,
  onCancel,
  onSubmit,
}: {
  slot: CalendarPlanSlot;
  brandId: string;
  phone: boolean;
  initial: WriteOwnDraft;
  /** The desktop's way back; "Back to the three ideas" beside them. */
  backLabel?: string;
  busy?: boolean;
  onCancel: () => void;
  onSubmit: (draft: WriteOwnDraft) => void;
}) {
  const [title, setTitle] = useState(initial.title);
  const [hooks, setHooks] = useState<string[]>(initial.hooks.length ? initial.hooks : [""]);
  const [description, setDescription] = useState(initial.description);
  const [objective, setObjective] = useState(initial.objective);
  const [references, setReferences] = useState<MediaChoice[]>(initial.references);
  const [picking, setPicking] = useState(false);

  const format = `${MEDIA_TYPE_LABEL[slot.mediaType]} ${displayAspectRatio(slot)}`;
  const day = shortDate(slot.scheduledAt);
  const planned = `Planned as ${format}${day ? ` on ${day}` : ""}.`;
  // Blank hook rows are dropped on submit; the first must be written.
  const writtenHooks = hooks.map((hook) => hook.trim()).filter(Boolean);
  const ready = !!title.trim() && writtenHooks.length > 0 && !!description.trim() && !busy;

  const submit = () => {
    if (!ready) return;
    onSubmit({ title: title.trim(), hooks: writtenHooks, description: description.trim(), objective: objective.trim(), references });
  };

  const setHookAt = (at: number, value: string) => setHooks((list) => list.map((hook, index) => (index === at ? value : hook)));
  const removeHookAt = (at: number) => setHooks((list) => (list.length > 1 ? list.filter((_, index) => index !== at) : [""]));

  const addButton =
    references.length < REFERENCES_MAX ? (
      <Button
        size="small"
        variant="secondary"
        buttonStyle="outline"
        icon={Plus}
        iconPosition="right"
        iconClassName="size-3.5"
        className="h-11 shrink-0"
        onPress={() => setPicking(true)}
      >
        {references.length ? "Add another" : "Add a reference"}
      </Button>
    ) : null;

  return (
    <section aria-labelledby="write-own-title" className={cn("flex w-full flex-col gap-[18px]", phone ? "pb-28" : "mx-auto max-w-[760px]")}>
      {phone ? (
        // The phone's stepper: three ideas, then yours (Figma 08, Phone · write your own idea).
        <div className="flex flex-col gap-2">
          <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-co-muted">Your own idea</p>
          <div className="flex gap-1" aria-hidden="true">
            {[0, 1, 2, 3].map((step) => (
              <span key={step} className={cn("h-0.5 flex-1", step === 3 ? "bg-co-accent" : "bg-co-line2")} />
            ))}
          </div>
        </div>
      ) : null}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2.5">
          <Sparkles className="size-[18px] shrink-0 text-co-accent" strokeWidth={1.7} aria-hidden="true" />
          <h2 id="write-own-title" className="min-w-0 flex-1 font-section text-[24px] font-bold leading-[29px] text-co-text">
            Write your own idea
          </h2>
          {phone ? null : (
            <button
              type="button"
              onClick={onCancel}
              className="shrink-0 text-[13px] font-medium text-co-text2 outline-none transition-colors hover:text-co-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent"
            >
              {backLabel}
            </button>
          )}
        </div>
        <p className="text-[14px] leading-[1.5] text-co-text2">Write the idea your way. Creo keeps it as you write it.</p>
      </div>

      <KitInput label="Title" value={title} maxLength={TITLE_MAX} dir="auto" onChange={(event) => setTitle(event.target.value)} />
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <p className="text-[13px] font-semibold leading-[18px] text-co-text">Hooks</p>
          <p className="text-[12px] leading-4 text-co-muted">The first is the one {phone ? "shown" : "the client sees"} · up to {HOOKS_MAX}</p>
        </div>
        {hooks.map((hook, index) => (
          <div key={index} className="flex items-center gap-2">
            <KitInput
              aria-label={`Hook ${index + 1}`}
              value={hook}
              maxLength={HOOK_MAX}
              dir="auto"
              wrapperClassName="min-w-0 flex-1"
              onChange={(event) => setHookAt(index, event.target.value)}
            />
            <KitIconButton
              icon={X}
              aria-label={`Remove hook ${index + 1}`}
              disabled={hooks.length === 1 && !hook}
              onClick={() => removeHookAt(index)}
            />
          </div>
        ))}
        {hooks.length < HOOKS_MAX ? (
          <KitTextAction icon={Plus} tone="accent" className="self-start" onClick={() => setHooks((list) => [...list, ""])}>
            Add a hook
          </KitTextAction>
        ) : null}
      </div>
      <KitTextarea
        label="Description"
        value={description}
        maxLength={DESCRIPTION_MAX}
        rows={3}
        dir="auto"
        onChange={(event) => setDescription(event.target.value)}
      />
      <KitTextarea
        label="Objective, optional"
        hint="What the post is for. Shows on the slide's Objective card."
        value={objective}
        maxLength={OBJECTIVE_MAX}
        rows={2}
        dir="auto"
        onChange={(event) => setObjective(event.target.value)}
      />

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <p className="text-[13px] font-semibold leading-[18px] text-co-text">Reference, optional</p>
          {phone ? null : <p className="text-[12px] leading-4 text-co-muted">Pick from References or paste a link</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          {references.map((choice, index) => (
            <ReferenceChip key={`${choice.pick.kind}-${index}`} choice={choice} onRemove={() => setReferences((list) => list.filter((_, at) => at !== index))} />
          ))}
          {addButton}
        </div>
      </div>

      {phone ? (
        <>
          <p className="text-[12.5px] text-co-muted">{planned}</p>
          <p className="text-[12.5px] leading-[17px] text-co-muted">Want Creo to write it? Ask in the side chat.</p>
          <div className="co-post-glass co-post-bottombar fixed inset-x-0 bottom-0 z-30 flex gap-2 border-t border-co-line">
            <Button variant="secondary" buttonStyle="outline" className="h-11 text-[14px]" onPress={onCancel}>
              Back
            </Button>
            <Button
              className="h-11 flex-1 text-[14px]"
              icon={Sparkles}
              iconPosition="right"
              iconClassName="size-3.5"
              isDisabled={!ready}
              loading={busy}
              onPress={submit}
            >
              Write brief
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2.5 pt-1.5">
            <p className="mr-auto text-[12.5px] leading-[17px] text-co-muted">{planned}</p>
            <Button size="small" icon={Sparkles} iconPosition="right" iconClassName="size-3.5" isDisabled={!ready} loading={busy} onPress={submit}>
              Write brief
            </Button>
          </div>
          <p className="text-[12.5px] leading-[17px] text-co-muted">Want Creo to write it? Ask in the side chat.</p>
        </>
      )}

      {picking ? (
        <IdeaMediaDialog
          brandId={brandId}
          isOpen
          mode="add"
          onClose={() => setPicking(false)}
          ideaLabel={null}
          plannedRatio={displayAspectRatio(slot)}
          onConfirm={(choice) => {
            if (!choice) return;
            setReferences((list) => {
              const key = JSON.stringify(choice.pick);
              return list.some((entry) => JSON.stringify(entry.pick) === key) ? list : [...list, choice].slice(0, REFERENCES_MAX);
            });
          }}
        />
      ) : null}
    </section>
  );
}
